import React from 'react';

import { currency, getFormattedAmount } from '../../helpers/currency';

import { Table, TableCell, Box, Section, TextField } from 'components';
import { Order } from 'data';
import { formatDateToMed } from 'helpers';
import useMediaQuery from 'hooks/mediaQuery/useMediaQuery';

const theadData = (isMobile: boolean) => {
  const mobileStyle = isMobile ? 'text-xs py-1 px-1' : 'w-[3%]';
  return [
    { Name: 'Refund Type', className: `${mobileStyle} text-left` },
    { Name: 'Refund', className: `${mobileStyle} text-center` },
    { Name: 'Refund Fee', className: `${mobileStyle} text-center` },
    { Name: 'Quantity', className: `${mobileStyle} text-center` },
    { Name: 'Subtotal', className: `${mobileStyle} text-center` },
  ];
};

const RefundTransactionDetails = ({
  order,
  vendorName,
  currency,
}: {
  order: Order;
  vendorName: string;
  currency: currency | undefined;
}) => {
  const isMobile = useMediaQuery();
  const refund = order?.refunds ? Object.values(order.refunds)[0] : null;

  const {
    refundFee = 0,
    refundSubTotalAmount = 0,
    refundTotalAmount = 0,
    createdOn,
    request = {
      refundType: '',
    },
  } = refund || {};
  const { refundType } = request;

  const subTotal = refundTotalAmount || 0;

  return isMobile ? (
    <>
      <div className={`w-[${window.innerWidth}px] bg-white px-4 pt-2 mt-3 mx-[-24px] pb-3 mb-12`}>
        <div className='mt-2'>
          <div className='pb-3'>
            <TextField value={vendorName} label='Vendor' isDisabled />
          </div>
          <div className='pb-3'>
            <TextField value={formatDateToMed(order.createdOn)} label='Date' isDisabled />
          </div>
          <div className='pb-3'>
            <TextField value='Refund of Cashless Purchase' label='Transaction' isDisabled />
          </div>
          <div className='pb-0'>
            <TextField value={order.uuid} label='Order ID' isDisabled />
          </div>
        </div>

        <h2 className='text-lg my-4 text-gray-500'>Refund Summary</h2>

        <div className='rounded-lg border border-[#e8efef] p-3'>
          <div className='flex justify-between items-center mb-2'>
            <div>{refundType}</div>
            <div>{getFormattedAmount(refundSubTotalAmount, currency)}</div>
          </div>

          <div className='border-b border-gray-200 mb-2'></div>

          <div className='flex justify-between items-center mb-2'>
            <div>Subtotal</div>
            <div>{getFormattedAmount(refundSubTotalAmount, currency)}</div>
          </div>

          <div className='flex justify-between items-center mb-2'>
            <div>Tax</div>
            <div>{getFormattedAmount(refundFee, currency)}</div>
          </div>

          <div className='border-b border-gray-200 mb-2'></div>

          <div className='flex justify-between items-center'>
            <div>Total</div>
            <div>{getFormattedAmount(subTotal, currency)}</div>
          </div>
        </div>
      </div>
    </>
  ) : (
    <>
      <Box className='p-12 mt-[12px]'>
        <div className='md:flex md:justify-between'>
          <div className='md:w-1/2 md:pr-8'>
            <Section
              title='Details'
              titleClassName='text-xl md:text-3xl'
              className='col-span-12  justify-end'
              description='There are the specifics of your refund.'
            />
          </div>
          <div className='md:w-1/2 md:pr-8'>
            <div className='pb-3'>
              <TextField value={vendorName} label='Vendor' isDisabled />
            </div>
            <div className='pb-3'>
              <TextField value={formatDateToMed(createdOn || '')} label='Date' isDisabled />
            </div>
            <div className='pb-3'>
              <TextField value='Refund of Cashless Purchase' label='Transaction' isDisabled />
            </div>
            <div className='pb-3'>
              <TextField value={order.uuid} label='Order ID' isDisabled />
            </div>
          </div>
        </div>

        <div className='w-full h-[1px] bg-[#E8EAED] mt-6 lg:mb-12' />

        <Table theadData={theadData(isMobile)} className='w-full rounded-lg mb-12'>
          <tr>
            <TableCell
              className={`${
                isMobile ? 'py-1 px-1 text-xs' : 'py-4 px-6'
              } text-custom-primary text-base`}
              data={refundType.toUpperCase()}
            />
            <TableCell
              className={`${isMobile && 'text-xs px-1'} text-center text-base text-[#6F7FAF]`}
              data={getFormattedAmount(refundSubTotalAmount, currency)}
            />
            <TableCell
              className={`${isMobile && 'text-xs px-1'} text-center text-base text-[#6F7FAF]`}
              data={getFormattedAmount(refundFee, currency)}
            />
            <TableCell
              className={`${isMobile && 'text-xs px-1'} text-center text-[#6F7FAF]`}
              data={1}
            />
            <TableCell
              className={`${
                isMobile ? 'text-xs px-1' : 'py-4 px-6'
              } text-base text-[#6F7FAF] text-center`}
              data={getFormattedAmount(subTotal, currency)}
            />
          </tr>
        </Table>

        <div className='md:flex md:justify-start'>
          <div className='md:w-1/2 md:pr-8'>
            <Section
              title='Refund Summary'
              titleClassName='text-xl md:text-3xl'
              className='col-span-12  justify-end'
            />
          </div>
          <div className='md:w-1/3 md:pr-8'>
            <div className='flex justify-between text-[#6F7FAF]  pb-3'>
              <p>Total Refund</p>
              <p>{getFormattedAmount(refundTotalAmount, currency)}</p>
            </div>
            <div className='flex justify-between text-[#6F7FAF]  pb-3'>
              <p>Refund Fee</p>
              <p>{getFormattedAmount(refundFee, currency)}</p>
            </div>
            <div className='w-full h-[1px] bg-[#E8EAED] mb-3' />

            <div className='flex justify-between pb-3'>
              <p className='pb-6 font-bold  text-lg'>Total</p>
              <p className='pb-6 font-bold  text-lg'>{getFormattedAmount(subTotal, currency)}</p>
            </div>
          </div>
        </div>
      </Box>
    </>
  );
};

export default RefundTransactionDetails;
